import { EntityId, IAttachmentDocument } from '@appshared';

export interface IReconcileTicketOverview {
  ticketId?: EntityId;
  brand?: string;
  customerInfo?: ICustomer;
  shippingMethod?: string;
  shippingFee?: number;
  orderDate?: Date;
  transactionId?: string;
  orderValue?: string;
  orderItems?: IOrderItem[];
  discountFee?: number;
  paymentValue?: string;
  reconcileStatus?: ReconcileStatus;
  status?: TicketStatus;
  createdDate?: Date;
  updatedDate?: Date;
  dueDate?: Date;
  orderDateString?: string;
}

export interface IReconcileTicketDetail {
  ticketId?: EntityId;
  brand?: string;
  reconcileStatus?: ReconcileStatus;
  status?: TicketStatus;
  orderInfo?: IOrder;
  paymentInfo?: IPayment;
  reason?: string;
  reasonExplanation?: string;
  comments?: IComment[];
  activityLogs?: IReconcileLog[];
}

export interface IReconcileLog {
  activityDate: string;
  contentCode: string;
  contents: string;
}

export interface IComment {
  id?: EntityId;
  parentCommentId?: string;
  author: {
    userId?: EntityId;
    userName?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  };
  title?: string;
  detail?: string;
  attachments?: IAttachmentDocument[];
  attachmentIds?: string[];
  createdDate?: string;
  avatar?: string;
}

// for new comment, before uploading to server
export interface INewComment {
  detail: string;
  attachments: File[];
  parentCommentId: string;
}

export interface IOrder {
  orderCode: string;
  transactionId: string;
  orderDate: string | number;
  status: string;
  shippingMethod: string;
  total: number;
  shippingFee: number;
  discountCode: string;
  discountFee: number;
  customerInfo: ICustomer;
  shippingAddress: IShippingAddress;
  orderItems: IOrderItem[];
}

export interface IShippingAddress {
  id: string;
  contact: string;
  street: string;
  addressLine2: string;
  country: string;
  city: string;
  state: string;
  postCode: string;
  customAttributes: object;
  orderItems: IOrderItem[];
}

export interface IOrderItem {
  name?: string;
  sku?: string;
  quantity?: number;
  unitPrice?: number;
  customAttributes?: object;
  orderAttributes?: IOrderAttribute[];
}

export interface IOrderAttribute {
  id?: string;
  name?: string;
  option?: string;
}

export interface ICustomer {
  id: string;
  externalId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  avatar: string;
  email: string;
  phone: string;
  customAttributes: object;
}

export interface IPayment {
  referenceTxnId: string;
  date: string;
  type: string;
  gateway: string;
  grossAmount: number;
  netAmount: number;
  items: IOrderItem[];
  fromEmail: string;
  dateString: string;
  invoice: string;
  refTxnId: string;
}
export interface IReconcileTicketReasons {
  toBeReconcile?: IReconcileReason[];
  orderNotFound?: IReconcileReason[];
  transactionNotFound?: IReconcileReason[];
}

export interface IReconcileReason {
  code: string;
  description: string;
  name: string;
  names: { vi: string; en: string };
  type: string;
}

// ReconcileStatus means also ReconcileType: 4 types of reconciling tickets
// For RECONCILED type, TicketStatus will only have COMPLETED value
export enum ReconcileStatus {
  RECONCILED = 'RECONCILED',
  TO_BE_RECONCILE = 'TO_BE_RECONCILE',
  ORDER_NOT_FOUND = 'ORDER_NOT_FOUND',
  TX_NOT_FOUND = 'TX_NOT_FOUND',
  EMPTY = '',
}

export enum OrderStatus {
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  REFUNDED = 'REFUNDED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  ONHOLD = 'ON_HOLD',
  EMPTY = '',
}

export enum TicketStatus {
  PENDING = 'PENDING', // 'Unprocessed'
  REQUESTMODIFICATION = 'REQUEST_MODIFICATION', // 'Adjustment needed'
  WAITINGAPPROVAL = 'WAITING_APPROVAL', // 'Pending Approval'
  COMPLETED = 'COMPLETED', // 'Processed'
  EMPTY = '', // TODO: confirm about this status?
}

export enum ReconcileTicketView {
  TO_BE_RECONCILE_DATA = 'TO_BE_RECONCILE_DATA',
  TRANSACTION_NOT_FOUND = 'TRANSACTION_NOT_FOUND',
  ORDER_NOT_FOUND = 'ORDER_NOT_FOUND',
  MASTER_LIST = 'MASTER_LIST',
}

export enum ConfirmationReasons {
  DIFFERENCE_QUANTITY = 'DIFFERENCE_QUANTITY',
  DIFFERENCE_COUPON_DISCOUNT = 'DIFFERENCE_COUPON_DISCOUNT',
  GIFT_ORDER = 'GIFT_ORDER',
  DUPLICATE_ORDER = 'DUPLICATE_ORDER',
  PAY_ANOTHER_METHOD = 'PAY_ANOTHER_METHOD',
  NO_ORDER = 'NO_ORDER',
  NO_PAYMENT = 'NO_PAYMENT',
  REFUNDING_ORDER = 'REFUNDING_ORDER',
  ADDITIONAL_PAYMENT = 'ADDITIONAL_PAYMENT',
  OTHERS = 'OTHERS',
}
